/**
 * Turning two-step sign-in on and off from Profile (UI-HONEST: "Enroll MFA"
 * and "Manage MFA" had no handler). The API is the person's own, taken from
 * the token (`kernel/identity/mfa.controller.ts`):
 *
 * - The current password comes first (D983): a session alone is not enough to
 *   turn two-step sign-in on, or someone holding a session left open could
 *   put their own phone on the account and lock the person out.
 * - `POST /v1/mfa/enroll {currentPassword}` starts: it answers the setup key,
 *   an `otpauth://` link, the recovery codes and a confirmation token, once.
 *   The factor is stored unconfirmed, so starting and walking away changes
 *   nothing about how the person signs in. The password is dropped from the
 *   form's state as soon as the request is sent.
 * - `POST /v1/mfa/confirm {code, confirmationToken}` with a code from the app
 *   turns it on; only then does sign-in ask for the second step. The token
 *   ties the confirmation to that enrolment and this session for ten
 *   minutes; when the API answers 403 REAUTHENTICATION_REQUIRED (too late,
 *   or started elsewhere) the form goes back to the password step, whose
 *   words say why.
 * - `POST /v1/mfa/disable` with a current code or a recovery code turns it
 *   off.
 *
 * A wrong password (401 AUTHENTICATION_FAILED), a wrong code (403
 * AUTHENTICATION_FAILED) or a deployment with no MFA key
 * (503 FEATURE_NOT_CONFIGURED) is shown in the person's language; nothing is
 * reported as done until the API says so. Afterwards `GET /v1/users/me` is
 * read again, so the badge shows what the server holds.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Field, Input } from "@/components/ui";
import { ErrorText } from "@/components/ErrorText";
import { post, tenantKey } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";

/** `POST /v1/mfa/enroll`'s answer, shown once. */
interface Enrolment {
  secret: string;
  provisioningUri: string;
  backupCodes: string[];
  /** Ties the confirmation to this enrolment and session (D983). */
  confirmationToken: string;
}

/** The API's answer when the password has to be entered again (D983). */
const REAUTHENTICATION_REQUIRED = "REAUTHENTICATION_REQUIRED";

/** An authenticator app's codes are six digits (RFC 6238 as Atlas uses it). */
const CODE_DIGITS = 6;

export function TwoStepSetup({
  enabled,
  onDone,
}: {
  /** Whether the person has two-step sign-in on now (`mfaEnabled`). */
  enabled: boolean;
  /** Tell the page what happened, in words, once the server has done it. */
  onDone: (message: string) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const refreshMe = () =>
    queryClient.invalidateQueries({ queryKey: tenantKey(["me"]) });

  const run = <T,>(call: () => Promise<T>, then: (result: T) => void) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    call()
      .then(then)
      .catch((err: unknown) => setError(err))
      .finally(() => setBusy(false));
  };

  const close = () => {
    setOpen(false);
    setEnrolment(null);
    setCode("");
    setPassword("");
    setError(null);
  };

  /** Back to the password step: the enrolment's proof is no longer good. */
  const reauthenticate = () => {
    setEnrolment(null);
    setCode("");
    setError(null);
  };

  const failure =
    error !== null ? (
      <div
        role="alert"
        style={{ color: "var(--atlas-danger)", fontSize: 13, marginBlock: 8 }}
      >
        <ErrorText error={error} />
      </div>
    ) : null;

  if (!open) {
    return (
      <>
        <Button size="sm" onClick={() => setOpen(true)}>
          {enabled ? t("profile.manageMfa") : t("profile.enrollMfa")}
        </Button>
        {failure}
      </>
    );
  }

  if (enabled) {
    return (
      <form
        aria-label={t("mfaSetup.disableTitle")}
        onSubmit={(event) => {
          event.preventDefault();
          run(
            () => post("/mfa/disable", { code: code.trim() }),
            () => {
              close();
              void refreshMe();
              onDone(t("mfaSetup.disabled"));
            },
          );
        }}
      >
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBlock: "0 8px" }}>
          {t("mfaSetup.disableTitle")}
        </h3>
        <p style={{ fontSize: 13, marginBlock: "0 8px" }}>
          {t("mfaSetup.disableBody")}
        </p>
        <Field label={t("mfaSetup.disableCodeLabel")} required>
          <Input
            value={code}
            required
            autoComplete="one-time-code"
            dir="ltr"
            onChange={(e) => setCode(e.target.value)}
          />
        </Field>
        {failure}
        <div style={{ display: "flex", gap: 8 }}>
          <Button type="submit" variant="danger" disabled={busy}>
            {busy ? t("mfaSetup.checking") : t("mfaSetup.turnOff")}
          </Button>
          <Button disabled={busy} onClick={close}>
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    );
  }

  if (!enrolment) {
    // Step 0: the current password, then the start (or its refusal, shown).
    return (
      <form
        aria-label={t("mfaSetup.setupTitle")}
        onSubmit={(event) => {
          event.preventDefault();
          const currentPassword = password;
          setPassword("");
          run(
            () => post<Enrolment>("/mfa/enroll", { currentPassword }),
            (result) => setEnrolment(result),
          );
        }}
      >
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBlock: "0 8px" }}>
          {t("mfaSetup.setupTitle")}
        </h3>
        <p style={{ fontSize: 13, marginBlock: "0 8px" }}>
          {t("errors.REAUTHENTICATION_REQUIRED")}
        </p>
        <Field label={t("auth.login.password")} required>
          <Input
            type="password"
            value={password}
            required
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <p role="status" style={{ fontSize: 13 }}>
          {busy ? t("mfaSetup.starting") : null}
        </p>
        {failure}
        <div style={{ display: "flex", gap: 8 }}>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? t("mfaSetup.checking") : t("auth.mfa.submit")}
          </Button>
          <Button disabled={busy} onClick={close}>
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form
      aria-label={t("mfaSetup.setupTitle")}
      onSubmit={(event) => {
        event.preventDefault();
        if (busy) return;
        setBusy(true);
        setError(null);
        post("/mfa/confirm", {
          code: code.trim(),
          confirmationToken: enrolment.confirmationToken,
        })
          .then(() => {
            close();
            void refreshMe();
            onDone(t("mfaSetup.enabled"));
          })
          .catch((err: unknown) => {
            // Too late, or not this enrolment: the password step again, whose
            // words say why. Any other refusal is shown here.
            if ((err as { code?: unknown })?.code === REAUTHENTICATION_REQUIRED)
              reauthenticate();
            else setError(err);
          })
          .finally(() => setBusy(false));
      }}
    >
      <h3 style={{ fontSize: 15, fontWeight: 600, marginBlock: "0 8px" }}>
        {t("mfaSetup.setupTitle")}
      </h3>
      <p style={{ fontSize: 13, marginBlock: "0 4px" }}>
        {t("mfaSetup.step1")}
      </p>
      <div className="atlas-label">{t("mfaSetup.keyLabel")}</div>
      <p
        className="atlas-mono"
        style={{ wordBreak: "break-all", marginBlock: "0 4px" }}
      >
        <bdi dir="ltr">{enrolment.secret}</bdi>
      </p>
      <p style={{ marginBlock: "0 12px" }}>
        <a href={enrolment.provisioningUri}>{t("mfaSetup.openApp")}</a>
      </p>
      <p style={{ fontSize: 13, marginBlock: "0 4px" }}>
        {t("mfaSetup.step2")}
      </p>
      <div className="atlas-label">{t("mfaSetup.recoveryCodes")}</div>
      <ul
        className="atlas-mono"
        dir="ltr"
        style={{ marginBlock: "0 12px", paddingInlineStart: 20 }}
      >
        {enrolment.backupCodes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <p style={{ fontSize: 13, marginBlock: "0 4px" }}>
        {t("mfaSetup.step3", { digits: CODE_DIGITS })}
      </p>
      <Field label={t("mfaSetup.codeLabel")} required>
        <Input
          value={code}
          required
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern={`[0-9]{${CODE_DIGITS}}`}
          maxLength={CODE_DIGITS}
          dir="ltr"
          onChange={(e) => setCode(e.target.value)}
        />
      </Field>
      {failure}
      <div style={{ display: "flex", gap: 8 }}>
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? t("mfaSetup.checking") : t("mfaSetup.turnOn")}
        </Button>
        <Button disabled={busy} onClick={close}>
          {t("common.cancel")}
        </Button>
      </div>
    </form>
  );
}
